import { useCallback } from 'react';

import { startOfDay } from 'date-fns';

import useTimetablesStore from '@calendar/store/useTimetablesStore';
import {
  isOverLappingHelper,
  isWithinIntervalHelper,
  timetableFactory,
} from '@calendar/timetableHelpers';
import { CreateEventTimetable, EventTimetableOption } from '@event/types';

interface HandleTimeSlotChange {
  (
    timeSlotStart: Date,
    selectedEventTimeslotLength: EventTimetableOption | null,
    timetables: CreateEventTimetable[],
  ): void;
}

const useHandleTimeSlotChange = (): HandleTimeSlotChange => {
  const { removeTimetable, addTimetable } = useTimetablesStore();

  return useCallback(
    (
      timeSlotStart: Date,
      selectedEventTimeslotLength: EventTimetableOption | null,
      timetables: CreateEventTimetable[],
    ) => {
      const isFullDay = selectedEventTimeslotLength?.fullDay === true;
      const timeSlotStartHour = isFullDay
        ? startOfDay(timeSlotStart)
        : timeSlotStart;
      const timeSlotLength = selectedEventTimeslotLength?.tillMidnight
        ? undefined
        : selectedEventTimeslotLength?.duration;
      const updatedTimetable = timetableFactory(
        timeSlotStartHour,
        timeSlotLength,
        isFullDay,
      );

      let isSomeWithinInterval = false;

      timetables.forEach((timetable) => {
        const isOverLapping = isOverLappingHelper(timetable, updatedTimetable);
        const isWithinInInterval = isWithinIntervalHelper(
          timeSlotStartHour,
          timetable,
        );

        if (isWithinInInterval) {
          removeTimetable(timetable.uuid);

          isSomeWithinInterval = true;
        }

        if (isOverLapping) {
          removeTimetable(timetable.uuid);
        }
      });

      if (!isSomeWithinInterval) {
        addTimetable(updatedTimetable);
      }
    },
    [addTimetable, removeTimetable],
  );
};

export default useHandleTimeSlotChange;
